import { formatStatus, parseUTCDate } from '@/lib/utils'
import type { components } from '@/api/schema'

/** What an action item card shows at a glance (USA-1417). */
export type ActionCard = components['schemas']['ActionCardRead']
export type ActionFact = components['schemas']['ActionFactRead']
export type ActionSourceEmail = components['schemas']['ActionSourceEmailRead']
export type ActionSourceShipment = components['schemas']['ActionSourceShipmentRead']
export type ActionInvoice = components['schemas']['ActionInvoiceRead']
export type ActionPriority = ActionCard['priority']
type ProjectAction = components['schemas']['ProjectOpenActionRead']

const ROLE_LABELS: Record<string, string> = {
  sales: 'Sales',
  ops: 'Ops',
  operations: 'Ops',
  warehousing: 'Warehouse',
  accounting: 'Accounting',
  purchasing: 'Purchasing',
}

export function roleLabel(role: string) {
  return ROLE_LABELS[role] ?? formatStatus(role)
}

/** "now", "5m", "3h", "6d" since the item was raised. */
export function compactAge(createdAt: string, now: number = Date.now()) {
  const minutes = Math.max(0, Math.floor((now - parseUTCDate(createdAt).getTime()) / 60_000))
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

/** "Oct 30" for a YYYY-MM-DD date, with the year only when it isn't this year. */
export function shortDate(dateOnly: string) {
  const [year, month, day] = dateOnly.split('-').map(Number)
  const date = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  })
}

export function isPastDue(dateOnly: string) {
  const [year, month, day] = dateOnly.split('-').map(Number)
  const due = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return due < today
}

/** Items worked on the project page itself: Sales's "Review POs" (USA-1333) and the cancellation follow-up. */
export function isWorkedOnProject(action: ProjectAction) {
  if (action.action_source === 'customer_communication' && action.customer_thread_id != null) return false
  if (action.action_source === 'order_line' && action.order_uuid) return false
  return !action.po_uuid || action.action_type === 'po_review'
}

/** The stretch of work going on now, if someone marked the item In Progress (USA-1443). */
export function currentProgress(action: {
  status: string
  progress?: readonly { ended_at?: string | null }[] | null
}) {
  if (action.status !== 'open') return null
  const last = action.progress?.at(-1)
  return last && !last.ended_at ? last : null
}
